import { Microscope, Leaf, FlaskConical, ShieldCheck } from "lucide-react";
import { Container, Button } from "@/components/atoms";
import { FaqAccordionItem } from "@/components/molecules";
import { TrustVerification } from "@/components/sections";
import { siteFaqs } from "@/data";
import { Seo } from "@/components/seo";

const blocks = [
  {
    icon: Leaf,
    title: "What is CBD?",
    body: "CBD (cannabidiol) is a naturally occurring compound from the hemp plant. Unlike THC, it isn’t intoxicating — it’s studied for its supportive role in everyday wellness.",
  },
  {
    icon: FlaskConical,
    title: "Full-spectrum vs. isolate",
    body: "Full-spectrum retains the plant’s natural range of cannabinoids and terpenes for an “entourage effect.” Isolate is pure CBD only. Most of our drops are full-spectrum.",
  },
  {
    icon: Microscope,
    title: "Why testing matters",
    body: "CBD products vary wildly in accuracy. Every DrBurns’ ReLeaf batch is third-party tested — what’s on the label is what’s in the bottle, verified to 99.5%.",
  },
  {
    icon: ShieldCheck,
    title: "How to choose",
    body: "Start with your goal: daytime vs. nighttime, general wellness vs. a specific condition. Our condition-specific formulas take the guesswork out.",
  },
];

export default function Cbd101() {
  return (
    <>
      <Seo
        title="CBD 101"
        path="/cbd-101"
        description="A physician’s plain-English guide to CBD — what it is, full-spectrum vs. isolate, why testing matters, and how to choose."
      />

      <section className="bg-leaf-green/5 py-12">
        <Container className="text-center">
          <h1 className="font-condensed text-4xl font-bold text-night-blue">
            CBD 101
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-night-blue/70">
            A physician’s plain-English guide. Rooted in science, not folklore.
          </p>
        </Container>
      </section>

      <Container className="grid gap-6 py-12 md:grid-cols-2">
        {blocks.map((b) => (
          <div
            key={b.title}
            className="flex gap-4 rounded-card border border-night-blue/10 p-6"
          >
            <b.icon size={28} className="mt-1 shrink-0 text-leaf-green-ink" />
            <div>
              <h2 className="font-condensed text-xl font-bold text-night-blue">
                {b.title}
              </h2>
              <p className="mt-1 text-sm text-night-blue/70">{b.body}</p>
            </div>
          </div>
        ))}
      </Container>

      <TrustVerification />

      <Container className="py-12">
        <h2 className="mb-4 font-condensed text-2xl font-bold text-night-blue">
          Common questions
        </h2>
        <div className="mx-auto max-w-2xl">
          {siteFaqs
            .filter(
              (f) => f.category === "CBD 101" || f.category === "Products",
            )
            .map((f) => (
              <FaqAccordionItem
                key={f.id}
                question={f.question}
                answer={f.answer}
              />
            ))}
        </div>
        <div className="mt-8 text-center">
          <Button to="/shop">Shop Products</Button>
        </div>
      </Container>
    </>
  );
}
